/**
 * AISettings — Eva AI workforce assistant configuration.
 * All state and handlers live in Settings.js (parent). This component is pure JSX.
 * RULE: Do NOT add state here. Pass new props from Settings.js instead.
 *
 * PROVIDER constants are co-located here so future AI provider additions
 * only require editing this file.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Bot, CheckCircle, Wifi, WifiOff, AlertCircle, Loader2 } from 'lucide-react';

export const PROVIDER_LABELS = {
  openai:    'OpenAI',
  anthropic: 'Claude (Anthropic)',
  gemini:    'Gemini (Google)',
  xai:       'Grok / xAI',
  deepseek:  'DeepSeek',
  kimi:      'Kimi (Moonshot AI)',
  zai:       'Z.ai (Zhipu AI)',
};

export const PROVIDER_LINKS = {
  openai:    'https://platform.openai.com/api-keys',
  anthropic: 'https://console.anthropic.com/settings/keys',
  gemini:    'https://aistudio.google.com/app/apikey',
  xai:       'https://console.x.ai/',
  deepseek:  'https://platform.deepseek.com/api_keys',
  kimi:      'https://platform.moonshot.cn/console/api-keys',
  zai:       'https://open.bigmodel.cn/usercenter/apikeys',
};

export const PROVIDER_INSTRUCTIONS = {
  openai:    ['Open the provider setup page.', 'Sign in to your OpenAI account.', 'Create a new secret key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
  anthropic: ['Open the provider setup page.', 'Sign in to Anthropic Console.', 'Go to API Keys and create a new key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
  gemini:    ['Open the provider setup page.', 'Sign in to Google AI Studio.', 'Create a Gemini API key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
  xai:       ['Open the provider setup page.', 'Sign in to xAI Console.', 'Create an API key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
  deepseek:  ['Open the provider setup page.', 'Sign in to DeepSeek Platform.', 'Create an API key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
  kimi:      ['Open the provider setup page.', 'Sign in to Moonshot AI.', 'Create an API key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
  zai:       ['Open the provider setup page.', 'Sign in to Zhipu AI.', 'Create an API key.', 'Paste it in the field below.', 'Save, then click Test Connection.'],
};

export default function AISettings({
  aiKeyInput,
  setAiKeyInput,
  aiKeyVisible,
  setAiKeyVisible,
  aiEnabled,
  setAiEnabled,
  aiProvider,
  setAiProvider,
  aiStatus,
  setAiStatus,
  aiTestDetail,
  aiSaving,
  aiTesting,
  saveAiSettings,
  testAiConnection,
}) {
  return (
    /* data-settings-section="ai" (REQUIRED — do not remove) */
    <Card className="border-slate-200" data-testid="ai-config-card" data-settings-section="ai">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bot size={20} className="text-slate-600" />
          AI Configuration
        </CardTitle>
        <CardDescription>
          Connect your {PROVIDER_LABELS[aiProvider] || 'AI'} API key to enable Eva,
          your AI workforce assistant. Eva is available to owners and managers only.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="max-w-md space-y-4">
          {/* Status badge */}
          <div className="flex items-center gap-2 text-sm">
            {aiStatus === 'connected' && (
              <span className="flex items-center gap-1.5 text-green-600 font-medium" data-testid="ai-status-connected">
                <CheckCircle size={15} /> Connected
              </span>
            )}
            {aiStatus === 'configured' && (
              <span className="flex items-center gap-1.5 text-blue-600 font-medium" data-testid="ai-status-configured">
                <Wifi size={15} /> Key saved — click Test to verify
              </span>
            )}
            {(aiStatus === 'not_configured' || aiStatus === 'unknown') && (
              <span className="flex items-center gap-1.5 text-slate-400 font-medium" data-testid="ai-status-not-configured">
                <WifiOff size={15} /> Not configured
              </span>
            )}
            {aiStatus === 'invalid_key' && (
              <span className="flex items-center gap-1.5 text-red-500 font-medium" data-testid="ai-status-invalid">
                <AlertCircle size={15} /> {aiTestDetail || 'Invalid API key — check the key is correct and try again'}
              </span>
            )}
            {aiStatus === 'rate_limited' && (
              <span className="flex items-center gap-1.5 text-amber-600 font-medium" data-testid="ai-status-rate-limited">
                <AlertCircle size={15} /> {aiTestDetail || 'Rate limit exceeded — wait a minute and try again'}
              </span>
            )}
            {aiStatus === 'error' && (
              <span className="flex items-center gap-1.5 text-red-500 font-medium" data-testid="ai-status-error">
                <AlertCircle size={15} /> {aiTestDetail || 'Connection error — check your key is saved and valid'}
              </span>
            )}
          </div>

          {/* Provider dropdown */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
              AI Provider
            </Label>
            <select
              value={aiProvider}
              onChange={(e) => {
                setAiProvider(e.target.value);
                setAiStatus('not_configured');
              }}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="ai-provider-select"
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Claude (Anthropic)</option>
              <option value="gemini">Gemini (Google)</option>
              <option value="xai">Grok / xAI</option>
              <option value="deepseek">DeepSeek</option>
              <option value="kimi">Kimi (Moonshot AI)</option>
              <option value="zai">Z.ai (Zhipu AI)</option>
            </select>
          </div>

          {/* Setup link + instructions */}
          <div>
            <a
              href={PROVIDER_LINKS[aiProvider]}
              target="_blank"
              rel="noreferrer"
              className="text-orange-600 hover:underline text-sm font-medium"
              data-testid="ai-provider-setup-link"
            >
              Open {PROVIDER_LABELS[aiProvider]} setup page &rarr;
            </a>
            <ol className="text-xs text-slate-500 mt-2 space-y-1 list-decimal list-inside">
              {(PROVIDER_INSTRUCTIONS[aiProvider] || []).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          {/* API Key input */}
          <div>
            <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
              {PROVIDER_LABELS[aiProvider] || 'AI'} API Key
            </Label>
            <p className="text-xs text-slate-500 mb-2">
              Stored encrypted, never exposed to users.
            </p>
            <div className="relative">
              <Input
                type={aiKeyVisible ? 'text' : 'password'}
                value={aiKeyInput}
                onChange={(e) => {
                  setAiKeyInput(e.target.value);
                  if (aiStatus === 'connected') setAiStatus('configured');
                }}
                placeholder="Paste your API key here…"
                className="pr-10 font-mono text-sm"
                data-testid="ai-api-key-input"
              />
              <button
                type="button"
                onClick={() => setAiKeyVisible(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                data-testid="ai-key-toggle-visibility"
              >
                {aiKeyVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={saveAiSettings}
              disabled={aiSaving}
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="ai-save-btn"
            >
              {aiSaving ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Saving…</>
              ) : (
                'Save Key'
              )}
            </Button>
            <Button
              onClick={testAiConnection}
              disabled={aiTesting}
              size="sm"
              variant="outline"
              data-testid="ai-test-btn"
            >
              {aiTesting ? (
                <><Loader2 size={14} className="mr-1.5 animate-spin" /> Testing…</>
              ) : (
                'Test Connection'
              )}
            </Button>
          </div>

          <p className="text-xs text-slate-400 pt-1">
            Eva is a read-only assistant. It cannot modify schedules, staff records, or any data.
            {aiKeyInput && <span className="block text-amber-600 mt-0.5">Save your key first, then click Test Connection.</span>}
            <span className="block text-slate-400 mt-0.5">Test Connection verifies your saved provider settings, not unsaved changes above.</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
